import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNextButton,
    CarouselPreviousButton,
} from "@patient-watch/ui/carousel";
import { Card, CardContent } from "@patient-watch/ui";
import { AdminPasswordGate } from "../components/AdminPasswordGate";
import { AdminLayout } from "../components/AdminLayout";
import { useStudyTrackerData } from "../hooks/useStudyTrackerData";
import { StudyTrackerCalendar } from "../components/StudyTrackerCalendar";
import { StudyTrackerDayDialog } from "../components/StudyTrackerDayDialog";
import type { DayStats } from "../utils/studyTrackerHelpers";

export const Route = createFileRoute("/study-tracker")({
    component: StudyTrackerComponent,
});

function StudyTrackerComponent() {
    return (
        <AdminPasswordGate description="Enter the study admin password to view the study tracker.">
            <StudyTrackerContent />
        </AdminPasswordGate>
    );
}

function StudyTrackerContent() {
    const { data, isLoading, isError } = useStudyTrackerData();
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
    const [selectedDayStats, setSelectedDayStats] = useState<DayStats | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const currentMonth = dayjs();
    const months: Dayjs[] = [];
    for (let i = -2; i <= 2; i++) {
        months.push(currentMonth.add(i, "month"));
    }
    const currentMonthIndex = months.findIndex((month) => month.isSame(currentMonth, "month"));

    const handleDayClick = (date: Dayjs, dayStats: DayStats | null) => {
        if (dayStats) {
            setSelectedDate(date);
            setSelectedDayStats(dayStats);
            setDialogOpen(true);
        }
    };

    if (isLoading) {
        return (
            <AdminLayout>
                <div className="py-12 text-center">Loading study tracker...</div>
            </AdminLayout>
        );
    }

    if (isError || !data) {
        return (
            <AdminLayout>
                <Card className="mx-auto max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <h2 className="mb-4 font-bold text-red-600 text-2xl">Error</h2>
                            <p className="text-gray-600">Failed to load study tracker data.</p>
                        </div>
                    </CardContent>
                </Card>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="gap-4 grid grid-cols-1 md:grid-cols-3">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <div className="mb-1 font-medium text-slate-600 text-sm">Total Enrolled</div>
                                <div className="font-bold text-slate-900 text-2xl">{data.allStudyNumbers.length}</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <div className="mb-1 font-medium text-slate-600 text-sm">Days with Data</div>
                                <div className="font-bold text-slate-900 text-2xl">{data.days.length}</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <div className="mb-1 font-medium text-slate-600 text-sm">Average Completion</div>
                                <div className="font-bold text-slate-900 text-2xl">
                                    {data.days.length > 0
                                        ? Math.round(
                                              (data.days.reduce(
                                                  (sum, day) => sum + (day.total > 0 ? day.completed / day.total : 0),
                                                  0
                                              ) /
                                                  data.days.length) *
                                                  100
                                          )
                                        : 0}
                                    %
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="bg-white shadow-sm p-4 border border-slate-200 rounded-lg">
                    <Carousel opts={{ startIndex: currentMonthIndex }}>
                        <CarouselContent>
                            {months.map((month) => (
                                <CarouselItem key={month.format("YYYY-MM")}>
                                    <StudyTrackerCalendar
                                        month={month}
                                        daysStats={data.days}
                                        onDayClick={handleDayClick}
                                    />
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <div className="flex justify-between items-center mt-4">
                            <CarouselPreviousButton />
                            <CarouselNextButton />
                        </div>
                    </Carousel>
                </div>
            </div>

            <StudyTrackerDayDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                dayStats={selectedDayStats}
                date={selectedDate}
            />
        </AdminLayout>
    );
}
